import { Link } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { formatDate } from "@/lib/date";
import type { BlogPost } from "@/content/blog/posts";

/**
 * A single blog post card for the index grid. The whole card is one accessible
 * link (the title), so there is exactly one focusable target per card — no
 * nested or duplicate links (WCAG 2.4.4 / 4.1.2). The image is decorative
 * relative to the link text, so it is not itself a separate link.
 */
export function BlogCard({ post }: { post: BlogPost }) {
  return (
    <article className="card-surface card-surface-hover group relative flex h-full flex-col overflow-hidden">
      {post.photo && (
        <AspectRatio ratio={16 / 9} className="bg-muted">
          <img
            src={post.photo}
            alt={post.alt ?? ""}
            width={1200}
            height={630}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </AspectRatio>
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
          {post.tag && (
            <span className="badge-pill !px-3 !py-1 !text-[0.65rem]">{post.tag}</span>
          )}
          <time dateTime={post.date}>{formatDate(post.date)}</time>
        </div>
        <h2 className="font-display text-xl font-bold text-foreground">
          <Link
            to={`/blog/${post.slug}`}
            className="after:absolute after:inset-0 focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring group-hover:text-primary"
          >
            {post.title}
          </Link>
        </h2>
        <p className="mt-2 flex-1 text-base leading-relaxed text-muted-foreground">
          {post.excerpt}
        </p>
      </div>
    </article>
  );
}
